// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/pages/Operacional/PcpOrdens.tsx
//  PCP — Listagem de Ordens de Produção + Sugestões + Planejamento
// ════════════════════════════════════════════════════════════════════════════

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Trash2, Factory, PlayCircle, CheckCircle2,
  Eye, Pencil, ChevronLeft, ChevronRight, PauseCircle, XCircle,
  Lightbulb, CalendarDays, AlertTriangle, Printer,
} from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { pcpService } from '@/features/operacional/services/pcpService';
import { useDebounce } from '@/hooks/useDebounce';
import { PeriodoFiltro, usePeriodo } from '@/components/listas/PeriodoFiltro';
import { CabecalhoOrdenavel, useOrdenacao } from '@/components/listas/CabecalhoOrdenavel';
import { usePcp } from '@/features/operacional/hooks/usePcp';
import { useEstoqueReposicao } from '@/features/estoque/hooks/useEstoquePosicao';
import { calcularStatusPcp } from '@/features/operacional/types';
import type { PcpOrdemResumo, StatusPcp } from '@/features/operacional/types';
import { DeleteConfirmDialog } from '@/features/financeiro/components/DeleteConfirmDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

// ── Helpers de status ─────────────────────────────────────────────────────

const statusConfig: Record<StatusPcp, { label: string; className: string; icon: typeof Factory }> = {
  aberta:       { label: 'Aberta',       className: 'bg-blue-500/10 text-blue-500',    icon: Factory      },
  em_andamento: { label: 'Em Andamento', className: 'bg-orange-500/10 text-orange-500',icon: PlayCircle   },
  pausada:      { label: 'Pausada',      className: 'bg-yellow-500/10 text-yellow-600',icon: PauseCircle  },
  finalizada:   { label: 'Finalizada',   className: 'bg-green-500/10 text-green-500',  icon: CheckCircle2 },
  cancelada:    { label: 'Cancelada',    className: 'bg-red-500/10 text-red-500',      icon: XCircle      },
};

const PAGE_SIZES = [10, 20, 50, 100] as const;

// ── Aba: Sugestões de Produção ────────────────────────────────────────────

function TabSugestoes() {
  const navigate = useNavigate();
  const { data: reposicao, isLoading } = useEstoqueReposicao();

  const sugestoes = reposicao ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Lightbulb className="h-5 w-5 text-yellow-500" />
          Sugestões de Produção — Estoque Abaixo do Mínimo
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <p className="text-center py-8 text-muted-foreground">Carregando...</p>
        ) : sugestoes.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 text-green-500" />
            <p>Todos os produtos estão acima do estoque mínimo.</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground mb-4">
              {sugestoes.length} produto{sugestoes.length !== 1 ? 's' : ''} abaixo do estoque mínimo.
              Crie uma Ordem de Produção para suprir a necessidade.
            </p>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Produto</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead className="text-right">Saldo Atual</TableHead>
                    <TableHead className="text-right">Estoque Mínimo</TableHead>
                    <TableHead className="text-right">Qtd. Sugerida</TableHead>
                    <TableHead className="text-right">Ação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sugestoes.map((s) => (
                    <TableRow key={`${s.produtoId}-${s.localEstoqueId}`}>
                      <TableCell className="font-medium">{s.descricao}</TableCell>
                      <TableCell className="text-muted-foreground">{s.localDescricao}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">
                        {s.saldoAtual.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {s.estoqueMinimo.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right font-mono font-semibold text-orange-600">
                        {s.quantidadeRepor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => navigate('/operacional/pcp/nova', {
                            state: {
                              sugestao: {
                                produtoId:   s.produtoId,
                                descricao:   s.descricao,
                                quantidade:  s.quantidadeRepor,
                                referencia:  `Reposição: ${s.descricao}`,
                              },
                            },
                          })}
                        >
                          <Plus className="h-3 w-3" />Criar Ordem
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

// ── Aba: Planejamento por Período ─────────────────────────────────────────

function TabPlanejamento({ ordens }: { ordens: PcpOrdemResumo[] }) {
  const navigate = useNavigate();
  const hoje = new Date();
  const inicioDefault = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1).toISOString().slice(0, 7);
  const fimDefault    = new Date(hoje.getFullYear(), hoje.getMonth() + 2, 0).toISOString().slice(0, 7);

  const [mesInicio, setMesInicio] = useState(inicioDefault);
  const [mesFim,    setMesFim]    = useState(fimDefault);

  const ordensFiltradas = useMemo(() => {
    const ini = new Date(`${mesInicio}-01`);
    const fim = new Date(`${mesFim}-01`);
    fim.setMonth(fim.getMonth() + 1);
    return ordens.filter((o) => {
      const dt = new Date(o.dtPrevisaoEntrega);
      return dt >= ini && dt < fim;
    });
  }, [ordens, mesInicio, mesFim]);

  const porMes = useMemo(() => {
    const map = new Map<string, PcpOrdemResumo[]>();
    ordensFiltradas.forEach((o) => {
      const dt  = new Date(o.dtPrevisaoEntrega);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(o);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [ordensFiltradas]);

  const nomeMes = (ym: string) => {
    const [y, m] = ym.split('-');
    return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5" />Planejamento por Período — Previsão de Entrega
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input type="month" value={mesInicio} onChange={(e) => setMesInicio(e.target.value)} className="w-36 h-8 text-sm" />
              <span className="text-muted-foreground text-sm">até</span>
              <Input type="month" value={mesFim}    onChange={(e) => setMesFim(e.target.value)}    className="w-36 h-8 text-sm" />
            </div>
          </div>
        </CardHeader>
      </Card>

      {porMes.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            Nenhuma ordem com previsão de entrega no período selecionado.
          </CardContent>
        </Card>
      ) : (
        porMes.map(([mes, itens]) => {
          const abertas      = itens.filter((o) => !o.isIniciado && !o.isFinalizado && !o.isCancelado).length;
          const emAndamento  = itens.filter((o) => o.isIniciado && !o.isFinalizado && !o.isCancelado).length;
          const finalizadas  = itens.filter((o) => o.isFinalizado).length;
          const canceladas   = itens.filter((o) => o.isCancelado).length;
          return (
            <Card key={mes}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base capitalize">{nomeMes(mes)}</CardTitle>
                  <div className="flex gap-2 flex-wrap">
                    {abertas     > 0 && <Badge className="bg-blue-500/10 text-blue-500">{abertas} abertas</Badge>}
                    {emAndamento > 0 && <Badge className="bg-orange-500/10 text-orange-500">{emAndamento} em andamento</Badge>}
                    {finalizadas > 0 && <Badge className="bg-green-500/10 text-green-500">{finalizadas} finalizadas</Badge>}
                    {canceladas  > 0 && <Badge className="bg-red-500/10 text-red-500">{canceladas} canceladas</Badge>}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nº</TableHead>
                      <TableHead>Referência</TableHead>
                      <TableHead>Prev. Início</TableHead>
                      <TableHead>Prev. Entrega</TableHead>
                      <TableHead className="text-right">Qtd. Prevista</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Ação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {itens.map((o) => {
                      const status = calcularStatusPcp(o);
                      const cfg    = statusConfig[status];
                      const Icon   = cfg.icon;
                      return (
                        <TableRow key={o.id}>
                          <TableCell className="font-medium">#{o.numeroOrdem}</TableCell>
                          <TableCell>{o.referencia || <span className="text-muted-foreground">—</span>}</TableCell>
                          <TableCell>{new Date(o.dtPrevisaoInicio).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell>{new Date(o.dtPrevisaoEntrega).toLocaleDateString('pt-BR')}</TableCell>
                          <TableCell className="text-right">{o.quantidadePrevista}</TableCell>
                          <TableCell>
                            <Badge className={cfg.className}>
                              <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/operacional/pcp/${o.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}

// ── Aba: Listagem de Ordens ───────────────────────────────────────────────

function TabOrdens() {
  const navigate = useNavigate();
  const [searchTerm,    setSearchTerm]    = useState('');
  const [statusFiltro,  setStatusFiltro]  = useState<string>('todos');
  const [deletingId,    setDeletingId]    = useState<number | null>(null);
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);

  // Abre nos últimos 30 dias (pela emissão), mais recentes primeiro.
  const periodo   = usePeriodo('30dias', () => setPagina(1));
  const ordenacao = useOrdenacao<'emissao' | 'numero' | 'entrega'>('emissao', true, () => setPagina(1));
  // Busca e status vão ao servidor (antes filtravam só a página carregada): espera a digitação parar.
  const buscaEnviada = useDebounce(searchTerm.trim(), 400);

  const { ordens, total, totalPaginas, isLoading, isFetching, deleteOrdem } =
    usePcp({
      pagina, tamanhoPagina,
      filtros: {
        busca: buscaEnviada, status: statusFiltro, ...periodo.intervalo,
        ordenarPor: ordenacao.ordenarPor, decrescente: ordenacao.decrescente,
      },
    });

  const firstItem = (pagina - 1) * tamanhoPagina + 1;
  const lastItem  = Math.min(pagina * tamanhoPagina, total);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <CardTitle>Listagem de Ordens de Produção</CardTitle>
            <div className="flex flex-col sm:flex-row sm:flex-wrap md:justify-end gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Nº ou referência..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setPagina(1); }}
                  className="pl-9" />
              </div>
              <PeriodoFiltro estado={periodo} rotuloData="Emissão" />
              <Select value={statusFiltro} onValueChange={(v) => { setStatusFiltro(v); setPagina(1); }}>
                <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="aberta">Aberta</SelectItem>
                  <SelectItem value="em_andamento">Em Andamento</SelectItem>
                  <SelectItem value="pausada">Pausada</SelectItem>
                  <SelectItem value="finalizada">Finalizada</SelectItem>
                  <SelectItem value="cancelada">Cancelada</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <CabecalhoOrdenavel coluna="numero" ordenacao={ordenacao}>Nº</CabecalhoOrdenavel>
                  <TableHead>Referência</TableHead>
                  <CabecalhoOrdenavel coluna="emissao" ordenacao={ordenacao}>Emissão</CabecalhoOrdenavel>
                  <CabecalhoOrdenavel coluna="entrega" ordenacao={ordenacao}>Prev. Entrega</CabecalhoOrdenavel>
                  <TableHead>Prev. Início</TableHead>
                  <TableHead className="text-right">Qtd. Prevista</TableHead>
                  <TableHead className="text-right">Custo Previsto</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : ordens.length === 0 ? (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {periodo.periodo !== 'todos'
                      ? 'Nenhuma ordem neste período — amplie o período para ver as anteriores'
                      : 'Nenhuma ordem encontrada'}
                  </TableCell></TableRow>
                ) : (
                  ordens.map((o: PcpOrdemResumo) => {
                    const status = calcularStatusPcp(o);
                    const cfg    = statusConfig[status];
                    const Icon   = cfg.icon;
                    const podeEditar = !o.isFinalizado && !o.isCancelado;
                    return (
                      <TableRow key={o.id}>
                        <TableCell className="font-medium">#{o.numeroOrdem}</TableCell>
                        <TableCell>{o.referencia || <span className="text-muted-foreground">—</span>}</TableCell>
                        <TableCell>{new Date(o.dtEmissao).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{new Date(o.dtPrevisaoEntrega).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell>{new Date(o.dtPrevisaoInicio).toLocaleDateString('pt-BR')}</TableCell>
                        <TableCell className="text-right">{o.quantidadePrevista}</TableCell>
                        <TableCell className="text-right font-medium">
                          {o.custoPrevisto.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                        </TableCell>
                        <TableCell>
                          <Badge className={cfg.className}>
                            <span className="flex items-center gap-1"><Icon className="h-3 w-3" />{cfg.label}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1">
                            {podeEditar && (
                              <Button size="sm" variant="ghost"
                                onClick={() => navigate(`/operacional/pcp/${o.id}`)} title="Editar">
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" title="Ver detalhes"
                              onClick={() => navigate(`/operacional/pcp/${o.id}`)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" title="Imprimir ordem de produção"
                              onClick={() => pcpService.abrirPdf(o.id).catch((err) =>
                                toast.error('Não foi possível gerar o PDF da ordem.', { description: getApiErrorMessage(err) }))}>
                              <Printer className="h-4 w-4" />
                            </Button>
                            {!o.isIniciado && !o.isFinalizado && (
                              <Button size="sm" variant="ghost" onClick={() => setDeletingId(o.id)} title="Excluir">
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              {total === 0 ? 'Nenhum registro'
                : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
              {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
            </p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
                <Select value={String(tamanhoPagina)} onValueChange={(v) => { setTamanhoPagina(Number(v)); setPagina(1); }}>
                  <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
                {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
                  let page: number;
                  if      (totalPaginas <= 5)         page = i + 1;
                  else if (pagina <= 3)                page = i + 1;
                  else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
                  else                                 page = pagina - 2 + i;
                  return (
                    <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                      size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
                  );
                })}
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
                <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <DeleteConfirmDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        onConfirm={() => { if (deletingId) { deleteOrdem(deletingId); setDeletingId(null); } }}
        title="Excluir Ordem de Produção"
        description="Tem certeza que deseja excluir esta ordem de produção?"
      />
    </>
  );
}

// ── Componente principal ──────────────────────────────────────────────────

const PcpOrdens = () => {
  const navigate = useNavigate();

  // Sem filtros: as 200 ordens mais recentes alimentam o Planejamento; os KPIs vêm do servidor
  // (`resumo`) e contam TODAS as ordens, não só as 200 carregadas.
  const { ordens, resumo, isLoading } = usePcp({ pagina: 1, tamanhoPagina: 200 });

  const totalAbertas     = resumo?.abertas       ?? 0;
  const totalEmAndamento = resumo?.emAndamento   ?? 0;
  const totalFinalizadas = resumo?.finalizadas   ?? 0;
  const custoPrevTotal   = resumo?.custoPrevisto ?? 0;

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-foreground">PCP — Produção</h1>
          <p className="text-muted-foreground mt-1">Planejamento e Controle da Produção</p>
        </div>
        <Button className="gap-2" onClick={() => navigate('/operacional/pcp/nova')}>
          <Plus className="h-4 w-4" />Nova Ordem
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <Factory className="h-4 w-4" />Abertas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-500">{isLoading ? '—' : totalAbertas}</div>
            <p className="text-xs text-muted-foreground mt-1">Aguardando início</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <PlayCircle className="h-4 w-4" />Em Andamento
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-500">{isLoading ? '—' : totalEmAndamento}</div>
            <p className="text-xs text-muted-foreground mt-1">Em produção</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" />Finalizadas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-500">{isLoading ? '—' : totalFinalizadas}</div>
            <p className="text-xs text-muted-foreground mt-1">Concluídas</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />Custo Previsto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-foreground">
              {isLoading ? '—' : custoPrevTotal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
            </div>
            <p className="text-xs text-muted-foreground mt-1">Ordens ativas</p>
          </CardContent>
        </Card>
      </div>

      {/* Abas */}
      <Tabs defaultValue="ordens">
        <TabsList>
          <TabsTrigger value="ordens" className="gap-2">
            <Factory className="h-4 w-4" />Ordens
          </TabsTrigger>
          <TabsTrigger value="sugestoes" className="gap-2">
            <Lightbulb className="h-4 w-4" />Sugestões de Produção
          </TabsTrigger>
          <TabsTrigger value="planejamento" className="gap-2">
            <CalendarDays className="h-4 w-4" />Planejamento
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ordens" className="mt-4">
          <TabOrdens />
        </TabsContent>

        <TabsContent value="sugestoes" className="mt-4">
          <TabSugestoes />
        </TabsContent>

        <TabsContent value="planejamento" className="mt-4">
          <TabPlanejamento ordens={ordens} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PcpOrdens;
